/**
 * apiFetch — the one HTTP call everything else goes through.
 *
 * Responsibilities (audit phase 09 §2 + §5 + §8):
 *   1. Resolve `path` against NEXT_PUBLIC_API_BASE_URL.
 *   2. Inject `Authorization: Bearer <jwt>` from the auth store
 *      (lib/auth/store.ts) — own-auth HS256 token, iss=lexai.  Caller
 *      may pass `skipAuth: true` for the public /auth/* endpoints, or
 *      override with an explicit `getToken` for the OAuth callback path.
 *   3. Auto-generate an Idempotency-Key for POST/PUT/PATCH/DELETE unless
 *      the caller provides their own.
 *   4. Parse the response body, map errors to typed classes (see errors.ts).
 *   5. Backoff + retry on 429 (`retry_after_seconds`) and 5xx (exponential).
 *      GET / HEAD / idempotent requests retry on network errors too.
 *
 * What it deliberately does NOT do:
 *   - Redirect on 402 — that's a UX decision for the component.  Throw
 *     PlanGateError and let the caller route to /subscribe.
 *   - Cache responses — TanStack Query owns that.
 */
import { getAuthToken } from '@/lib/auth/store';

import type { ApiErrorDetail } from './errors';
import { errorFromResponse, NetworkError, RateLimitError } from './errors';
import { generateIdempotencyKey, isMutation } from './idempotency';
import type { ApiFetchOptions } from './types';

const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_RETRY_BASE_MS = 500;
/** 5xx + network failures back off exponentially: 500ms → 1s → 2s. */
const backoffMs = (attempt: number, base = DEFAULT_RETRY_BASE_MS): number => base * 2 ** attempt;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(t);
      resolve();
    });
  });
}

/** Resolve to the base URL; throw early if misconfigured. */
function resolveUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = process.env['NEXT_PUBLIC_API_BASE_URL'];
  if (!base) {
    throw new Error('apiFetch: NEXT_PUBLIC_API_BASE_URL is not set. See .env.local.example.');
  }
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

/** Safely parse a JSON body; fall back to null on non-JSON / empty. */
async function parseJsonSafely(res: Response): Promise<ApiErrorDetail | null> {
  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) return null;
  try {
    return (await res.json()) as ApiErrorDetail;
  } catch {
    return null;
  }
}

/**
 * Core fetch.  Typed generics let callers get a typed response without
 * writing `as Whatever` everywhere:
 *
 *     const me = await apiFetch<UserMe>('/auth/me')
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const {
    getToken,
    skipAuth = false,
    method,
    body,
    headers: extraHeaders,
    idempotencyKey,
    maxRetries = DEFAULT_MAX_RETRIES,
    signal,
    raw = false,
  } = options;

  const url = resolveUrl(path);
  const isMutationReq = isMutation(method);
  const shouldRetryNetwork = !isMutationReq; // GET/HEAD are safe to retry

  let attempt = 0;
  let lastError: Error | null = null;

  while (attempt <= maxRetries) {
    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    const headers = new Headers(extraHeaders);
    if (!skipAuth) {
      const jwt = getToken ? getToken() : getAuthToken();
      if (jwt) headers.set('Authorization', `Bearer ${jwt}`);
    }

    if (isMutationReq && !headers.has('Idempotency-Key')) {
      headers.set('Idempotency-Key', idempotencyKey ?? generateIdempotencyKey());
    }

    // Auto-set JSON content-type unless the caller is sending FormData.
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && !isFormData && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    const init: RequestInit = {
      method: method ?? (body === undefined ? 'GET' : 'POST'),
      headers,
      body:
        body === undefined
          ? undefined
          : isFormData
            ? body
            : typeof body === 'string'
              ? body
              : JSON.stringify(body),
      cache: 'no-store',
      signal,
    };

    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (err) {
      const cause = err instanceof Error ? err : new Error(String(err));
      lastError = cause;
      if (!shouldRetryNetwork || attempt === maxRetries) {
        throw new NetworkError(cause, url);
      }
      await sleep(backoffMs(attempt), signal);
      attempt++;
      continue;
    }

    // --- success ---
    if (res.ok) {
      if (raw) return res as unknown as T;
      if (res.status === 204) return undefined as T;
      const contentType = res.headers.get('content-type') ?? '';
      if (contentType.includes('application/json')) {
        return (await res.json()) as T;
      }
      // Binary or text response — surface as-is.
      return (await res.text()) as unknown as T;
    }

    const errBody = await parseJsonSafely(res);
    const err = errorFromResponse(res.status, errBody, url);

    // --- 429: honour retry_after_seconds, then retry ---
    if (err instanceof RateLimitError && attempt < maxRetries) {
      await sleep(err.retryAfterMs, signal);
      attempt++;
      continue;
    }

    // --- 5xx on a safe verb: exponential backoff ---
    if (res.status >= 500 && shouldRetryNetwork && attempt < maxRetries) {
      lastError = err;
      await sleep(backoffMs(attempt), signal);
      attempt++;
      continue;
    }

    throw err;
  }

  // Exhausted retries without success.
  throw lastError ?? new Error(`apiFetch: exhausted ${maxRetries} retries`);
}
